import axios, {parseAxiosPromise} from '@/axiosInstance'
import {z} from 'zod'
import {PreviewRecipient, SendMailPayload} from '@/features/mailer/types'
import {httpGetPatients} from '@/features/patients/services/patients.http'
import {httpGetUsers, HttpInfiniteUsersResponse} from '@/features/users/http/users.http'
import {PatientSmall} from '@/features/patients/types'
import {PaginationResponse} from '@/types'
import {captureException} from '@sentry/react'

const PatientPreviewResponse = z.object({
    data: z.array(PatientSmall),
    meta: PaginationResponse
})

// Send email
export interface HttpSendMailOptions {
    payload: SendMailPayload
    attachments?: File[]
}
export const httpSendMail = ({payload, attachments}: HttpSendMailOptions) => {
    const formData = new FormData()
    formData.append('subject', payload.subject)
    formData.append('body', payload.body)
    formData.append('recipients', JSON.stringify(payload.recipients))

    if (attachments?.length) {
        attachments.forEach(file => {
            formData.append('attachments', file)
        })
    }

    return axios.post('/mailer/send', formData, {
        headers: {'Content-Type': 'multipart/form-data'}
    })
}

// Preview recipients
export const PAGE_LIMIT = 50

type PreviewMeta = {total: number; currentPage: number; lastPage: number}
type PreviewPage = {data: PreviewRecipient[]; meta: PreviewMeta}

export const fetchPatientsPreview = async (
    recipients: SendMailPayload['recipients'],
    page: number
): Promise<PreviewPage> => {
    if (!recipients.patients) return {data: [], meta: {total: 0, currentPage: 1, lastPage: 1}}
    const res = await parseAxiosPromise({
        axiosPromise: httpGetPatients({
            params: {page, limit: PAGE_LIMIT},
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            payload: {filters: recipients.patients.filters as any}
        }),
        responseShape: PatientPreviewResponse,
        onZodError: captureException
    })
    return {
        data: res.data.map(p => ({
            id: p.id,
            fullName: p.fullName,
            email: p.contacts.find(c => c.type === 'email')?.contact ?? ''
        })),
        meta: {
            total: res.meta.total,
            currentPage: res.meta.currentPage,
            lastPage: res.meta.lastPage
        }
    }
}

export const fetchPatientsCount = async (recipients: SendMailPayload['recipients']): Promise<number> => {
    if (!recipients.patients) return 0
    const res = await parseAxiosPromise({
        axiosPromise: httpGetPatients({
            params: {page: 1, limit: 1},
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            payload: {filters: recipients.patients.filters as any}
        }),
        responseShape: PatientPreviewResponse,
        onZodError: captureException
    })
    return res.meta.total
}

export const fetchConsultantsCount = async (recipients: SendMailPayload['recipients']): Promise<number> => {
    if (!recipients.consultants) return 0
    const res = await parseAxiosPromise({
        axiosPromise: httpGetUsers({
            params: {page: 1, limit: 1},
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            payload: {filters: recipients.consultants.filters as any}
        }),
        responseShape: HttpInfiniteUsersResponse,
        onZodError: captureException
    })
    return res.meta.total
}

export const fetchConsultantsPreview = async (
    recipients: SendMailPayload['recipients'],
    page: number
): Promise<PreviewPage> => {
    if (!recipients.consultants) return {data: [], meta: {total: 0, currentPage: 1, lastPage: 1}}
    const res = await parseAxiosPromise({
        axiosPromise: httpGetUsers({
            params: {page, limit: PAGE_LIMIT},
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            payload: {filters: recipients.consultants.filters as any}
        }),
        responseShape: HttpInfiniteUsersResponse,
        onZodError: captureException
    })
    return {
        data: res.data.map(u => ({
            id: u.id,
            fullName: u.fullName,
            email: u.email
        })),
        meta: {
            total: res.meta.total,
            currentPage: res.meta.currentPage,
            lastPage: res.meta.lastPage
        }
    }
}
